/* eslint-disable */
// DnaSelect — single-value New-DNA select. Trigger + createPortal menu
// (Convention #126: escapes modal overflow/transform). Solid opaque popover
// with a 4-stop warm shadow, flip-up near the viewport bottom, optional
// in-menu search (auto when >8 options), has-value amber border, keyboard
// (Enter/Space open, Esc close), click-outside + scroll close. NOT the legacy
// multi-select FpField, NOT SearchableSelect (shared consumers, out of scope).
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import shell from './createDna.module.css';
import styles from './DnaSelect.module.css';

// Options may be string[] or {value,label}[].
function normalize(opts) {
  return (opts || []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
}

export default function DnaSelect({
  label, required = false, options = [], value = '', onChange,
  placeholder = 'Select…', searchable, error = false, disabled = false,
  fieldRef, ...rest
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const opts = useMemo(() => normalize(options), [options]);
  const selected = opts.find((o) => String(o.value) === String(value));
  // F1 — only OPTIONAL fields may be cleared to empty (see the trigger comment below).
  const clearable = !!selected && !required && !disabled;
  // Auto-enable search for long lists unless the caller forces it on/off.
  const useSearch = searchable != null ? searchable : opts.length > 8;

  const openMenu = () => {
    if (disabled) return;
    const r = triggerRef.current && triggerRef.current.getBoundingClientRect();
    setRect(r ? { left: r.left, top: r.top, bottom: r.bottom, width: r.width } : null);
    setQ('');
    setOpen(true);
  };
  const close = () => setOpen(false);
  const toggle = () => (open ? close() : openMenu());

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onDown = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      close();
    };
    // Reposition (NOT close) on scroll/resize so the portal menu stays anchored
    // to its trigger as the modal body scrolls — flip-up recomputes from the
    // fresh rect on the next render. Capture phase catches the modal body's own
    // scroll. Skip scrolls that originate inside the menu's option list (the
    // trigger hasn't moved — leave it where it is).
    const reposition = (e) => {
      if (e && e.target && menuRef.current && menuRef.current.contains && menuRef.current.contains(e.target)) return;
      const r = triggerRef.current && triggerRef.current.getBoundingClientRect();
      if (r) setRect({ left: r.left, top: r.top, bottom: r.bottom, width: r.width });
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', reposition, true); // capture → modal scroll container
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  const filtered = useSearch && q
    ? opts.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : opts;

  // Position: clamp on-screen + flip up when it would overflow the bottom edge.
  const MENU_W = rect ? Math.max(rect.width, 200) : 220;
  const estH = Math.min(12 + (useSearch ? 44 : 0) + Math.max(filtered.length, 1) * 36, 320);
  const left = rect ? Math.max(12, Math.min(rect.left, window.innerWidth - MENU_W - 12)) : 12;
  const overflowBottom = rect ? (rect.bottom + 6 + estH > window.innerHeight - 12) : false;
  const top = rect ? (overflowBottom ? Math.max(12, rect.top - 6 - estH) : rect.bottom + 6) : 0;

  const pick = (v) => { if (onChange) onChange(v); close(); };

  return (
    <div className={styles.field} ref={fieldRef}>
      {label && (
        <label className={styles.label}>{label}{required && <span className={styles.req}> *</span>}</label>
      )}
      <div
        ref={triggerRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={[
          styles.trigger,
          selected ? styles.hasValue : '',
          error ? styles.err : '',
          disabled ? styles.disabled : '',
          open ? styles.open : '',
        ].filter(Boolean).join(' ')}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        {...rest}
      >
        <span className={selected ? styles.val : styles.placeholder}>
          {selected ? selected.label : placeholder}
        </span>
        {/* F1 — deselect. Until now a pick could only ever be REPLACED, never unset. Optional
            fields clear back to '' (their initial state, which validation already handles);
            required fields deliberately have no clear (re-pick only) so the form can't be
            emptied into an invalid state. stopPropagation so clearing never opens the menu. */}
        {clearable && (
          <span
            className={styles.clear} role="button" tabIndex={0} aria-label={`Clear ${label || 'selection'}`} title="Clear"
            onClick={(e) => { e.stopPropagation(); if (onChange) onChange(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); if (onChange) onChange(''); } }}
          >
            <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </span>
        )}
        <svg className={styles.caret} viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {open && rect && createPortal(
        <div className={shell.createDnaRoot} ref={menuRef}>
          <div className={styles.menu} style={{ left, top, width: MENU_W }} role="listbox">
            {useSearch && (
              <div className={styles.searchWrap}>
                <input autoFocus className={styles.search} placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
            )}
            <div className={styles.list}>
              {filtered.length === 0 && <div className={styles.empty}>No matches</div>}
              {filtered.map((o) => (
                <div
                  key={o.value}
                  role="option"
                  aria-selected={String(o.value) === String(value)}
                  className={`${styles.opt} ${String(o.value) === String(value) ? styles.optSel : ''}`}
                  onClick={() => pick(o.value)}
                >
                  <span className={styles.optLabel}>{o.label}</span>
                  {String(o.value) === String(value) && (
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
